import React, { useEffect, useRef, useState } from "react";
import OrderHistoryHeader from "../../components/Table/TableHeader";
import OrderTabs from "../../components/Table/Tabs";
import { getOrderStatusColor } from "../../utils/getOrderStatusColor";
import { orderStatus, roles } from "../../constants/commonConstants";
import { currencyFormatter, formatOrderStatus } from "../../utils/formatting";
import { LuSquareArrowOutUpRight } from "react-icons/lu";
import Dropdown from "../../components/Form/Dropdown";
import Input from "../../components/Form/Input";
import { orderStatusForDropdown } from "../../constants/commonConstants";
import DataTable from "../../components/Table/Table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getTaskerOrders } from "../../store";
import { overRideStatus } from "../../utils/statusUtils";

const TaskerOrderHistory = ({
  textColor = "#555F7E",
  textCommonStyles = `font-inter font-normal text-level-6 leading-[22px] text-[#555F7E]`,
}) => {
  const navigate = useNavigate();

  // Manage filters state
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    searchTerm: searchParams.get("searchTerm") || "",
    status: searchParams.get("status") || "",
    activeTab: searchParams.get("activeTab") || "",
    multipleStatus: searchParams.get("multipleStatus") || "",
  });
  const [activeTab, setActiveTab] = useState(
    searchParams.get("activeTab") || ""
  );
  // For tracking the real-time filter changes before debouncing
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  // track previous filters for comparison
  const prevFiltersRef = useRef(JSON.stringify(filters));

  // Update URL params whenever filters change
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    const currentFiltersStr = JSON.stringify(filters);
    const prevFiltersStr = prevFiltersRef.current;

    // Update or remove parameters based on filter values
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "") {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });

    // Only reset page to 1 if filters actually changed
    if (currentFiltersStr !== prevFiltersStr) {
      newParams.set("page", "1");
      prevFiltersRef.current = currentFiltersStr;
    }

    setSearchParams(newParams);
  }, [filters]);

  // Debounce effect for applying filter changes
  useEffect(() => {
    // Only create debounce timer if userName has changed
    if (debouncedFilters.searchTerm === filters.searchTerm) return;

    const timer = setTimeout(() => {
      setActiveTab("");
      // Update filters with the debounced value
      setFilters((prev) => ({
        ...prev,
        searchTerm: debouncedFilters.searchTerm,
      }));
    }, 500);

    return () => clearTimeout(timer);
  }, [debouncedFilters.searchTerm]);

  const columns = [
    {
      title: "Job ID",
      dataIndex: "id",
      key: "id",
      sortable: false,
      render: (id) => <span className={textCommonStyles}>{id}</span>,
    },
    {
      title: "Job Title",
      dataIndex: "title",
      key: "title",
      render: (_, record) => (
        <span className={textCommonStyles}>{record?.title || ""}</span>
      ),
      minWidth: "250px",
    },
    {
      title: "Client Name",
      dataIndex: "user",
      key: "user",
      sortable: false,
      render: (user) => (
        <span className={textCommonStyles}>
          {user ? `${user?.firstName || ""} ${user?.lastName || ""}` : "N/A"}
        </span>
      ),
      minWidth: "150px",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record) => {
        const filteredStatus = overRideStatus(record, roles.TASKER);
        const statusColor = getOrderStatusColor(filteredStatus);
        return (
          <span className={textCommonStyles} style={{ color: statusColor }}>
            {filteredStatus && formatOrderStatus(filteredStatus)}
          </span>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "price",
      key: "amount",
      render: (price) => {
        return (
          <span className={textCommonStyles} style={{ color: "#4F4F4F" }}>
            {" "}
            {/*#219653*/}
            {currencyFormatter.format(price)}
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        return (
          <button
            className="flex items-center justify-center gap-[10px] w-[137px] h-[33px] bg-[#FAFAFA] border border-primary rounded-[8px] cursor-pointer"
            onClick={() => {
              navigate(`/service-pro/order-details/${record._id}`);
            }}
          >
            <span className="font-inter font-normal text-[14px] text-[#404145]">
              View More
            </span>
            <LuSquareArrowOutUpRight className="text-primary" size={18} />
          </button>
        );
      },
    },
  ];

  // Handle search input change - update the debounced value
  const handleSearchChange = (e) => {
    setDebouncedFilters((prev) => ({
      ...prev,
      searchTerm: e.target.value,
    }));
  };

  // Handle status filter change
  const handleStatusChange = (option) => {
    setFilters((prev) => ({
      ...prev,
      status: option?.id || "",
      multipleStatus: "", // Clear multiple status when single status is selected
      activeTab: "", // Clear active tab when status filter is used
    }));
    setActiveTab("");
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    const activeTab = tab === "all" ? "" : tab;
    setActiveTab(activeTab);

    const ongoingStatuses = [
      orderStatus.ORDER_IN_PROGRESS,
      orderStatus.IN_REVISION,
      orderStatus.ORDER_DELIVERED,
    ];
    const completedStatuses = [
      orderStatus.COMPLETED,
      orderStatus.COMPLAINT_RAISED,
      orderStatus.ISSUE_RESOLVED,
      orderStatus.COMPLAINT_RESOLVED,
      orderStatus.ISSUE_RAISED,
    ];

    setFilters((prev) => ({
      ...prev,
      activeTab: activeTab,
      status: "", // Clear single status when tab is selected
      multipleStatus:
        tab === "ongoing"
          ? ongoingStatuses.join(",")
          : tab === "completed"
          ? completedStatuses.join(",")
          : "", // Empty string for "all" tab
    }));
  };

  const SearchInput = (
    <Input
      type="text"
      placeholder="Search Orders by Job ID"
      className="w-full h-10 px-10 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700"
      searchIcon={true}
      onChange={handleSearchChange}
      value={debouncedFilters.searchTerm}
      isFilterSearch={true}
      title="Search Orders by Job ID"
    />
  );

  const StatusFilter = (
    <div className="flex items-center gap-3 w-full md:w-auto">
      <span className="text-sm font-medium text-primary-text whitespace-nowrap">
        Filter By
      </span>
      <Dropdown
        options={orderStatusForDropdown?.filter(it => {return (it?.id !== orderStatus?.COMPLAINT_RAISED && it?.id !== orderStatus?.COMPLAINT_RESOLVED)})}
        idKey="id"
        displayKey="label"
        wrapperClassName="w-full md:w-[200px] h-[40px] rounded-[5px] border-light-blue font-inter text-level-6"
        placeholder="Status"
        buttonClassName="rounded-[5px] h-full border-light-blue font-inter text-level-6 leading-[10px] py-[10px]"
        optionClassName="font-inter text-level-7 font-normal"
        onSelect={handleStatusChange}
        defaultOption={
          orderStatusForDropdown.find(
            (status) => status.id === filters.status
          ) || ""
        }
      />
    </div>
  );

  return (
    <div className="mt-[48px] mb-[88px] px-[20px] md:px-[40px] lg:px-[100px]">
      <div className="bg-white rounded-[20px] border border-light-blue p-5 lg:p-9 flex flex-col gap-9">
        <div className=" flex flex-col gap-6">
          <OrderHistoryHeader
            heading={"My Jobs"}
            filters={[SearchInput, StatusFilter]}
          />
          <OrderTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleTabChange={handleTabChange}
          />
          <DataTable
            columns={columns}
            fetchData={getTaskerOrders}
            rowKey="_id"
            filters={filters}
            initialPageSize={5}
            enableUrlParams={true}
            inputFilterKeys={["searchTerm"]}
            customQueryParams={(params) => {
              // Transform params for the API
              const apiParams = { ...params };

              // Handle search by ID
              if (apiParams.searchTerm) {
                if (filters.searchTerm === "") {
                  delete apiParams.searchTerm;
                }
                delete apiParams.multipleStatus;
                apiParams.searchTerm = apiParams.searchTerm;
              }

              // Handle multiple statuses vs single status
              if (
                apiParams.multipleStatus &&
                apiParams.multipleStatus.includes(",") &&
                filters.status === ""
              ) {
                if (
                  activeTab === "" &&
                  apiParams.multipleStatus.includes(",")
                ) {
                  delete apiParams.multipleStatus;
                }
                delete apiParams.status;
              } else if (apiParams.status) {
                if (filters.status === "") {
                  delete apiParams.status;
                }
                delete apiParams.multipleStatus;
              }

              // Remove UI-specific params
              delete apiParams.activeTab;

              return apiParams;
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default TaskerOrderHistory;
